import client from 'client'
import type {
  UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationRequest,
  UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationResponse,
  UpdateWirelessSurveyWirelessSurveySurveyIdPutPathParams,
  UpdateWirelessSurveyWirelessSurveySurveyIdPutQueryParams,
  UpdateWirelessSurveyWirelessSurveySurveyIdPut422,
} from '../../types/UpdateWirelessSurveyWirelessSurveySurveyIdPut.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const updateWirelessSurveyWirelessSurveySurveyIdPutMutationKey = () => [{ url: '/wireless_survey/{survey_id}' }] as const

export type UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationKey = ReturnType<typeof updateWirelessSurveyWirelessSurveySurveyIdPutMutationKey>

/**
 * @summary Update Wireless Survey
 * {@link /wireless_survey/:survey_id}
 */
export async function updateWirelessSurveyWirelessSurveySurveyIdPut(
  survey_id: UpdateWirelessSurveyWirelessSurveySurveyIdPutPathParams['survey_id'],
  data: UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationRequest,
  params?: UpdateWirelessSurveyWirelessSurveySurveyIdPutQueryParams,
  config: Partial<RequestConfig<UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationResponse,
    ResponseErrorConfig<UpdateWirelessSurveyWirelessSurveySurveyIdPut422>,
    UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationRequest
  >({ method: 'PUT', url: `/wireless_survey/${survey_id}`, params, data, ...requestConfig })
  return res
}

/**
 * @summary Update Wireless Survey
 * {@link /wireless_survey/:survey_id}
 */
export function useUpdateWirelessSurveyWirelessSurveySurveyIdPut(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationResponse>,
      ResponseErrorConfig<UpdateWirelessSurveyWirelessSurveySurveyIdPut422>,
      {
        survey_id: UpdateWirelessSurveyWirelessSurveySurveyIdPutPathParams['survey_id']
        data: UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationRequest
        params?: UpdateWirelessSurveyWirelessSurveySurveyIdPutQueryParams
      }
    >
    client?: Partial<RequestConfig<UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? updateWirelessSurveyWirelessSurveySurveyIdPutMutationKey()

  return useMutation<
    ResponseConfig<UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationResponse>,
    ResponseErrorConfig<UpdateWirelessSurveyWirelessSurveySurveyIdPut422>,
    {
      survey_id: UpdateWirelessSurveyWirelessSurveySurveyIdPutPathParams['survey_id']
      data: UpdateWirelessSurveyWirelessSurveySurveyIdPutMutationRequest
      params?: UpdateWirelessSurveyWirelessSurveySurveyIdPutQueryParams
    }
  >({
    mutationFn: async ({ survey_id, data, params }) => {
      return updateWirelessSurveyWirelessSurveySurveyIdPut(survey_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}